import { useQuery } from "@tanstack/react-query";

import {
  getIncentivesByPlatformIdReferralHistoryByPersonId,
  getIncentivesByPlatformLocationIdActiveSignupReward,
  getIncentivesByPlatformLocationIdAvailableReferralReward,
  getPlatformCurrent,
  getUser,
} from "api/methods";
import { Models } from "@/api/types";

type Result = {
  showReferralRewards: boolean;
  showLegacyReferral: boolean;
  referralRewardForPlatformLocation?: Models<"DisplayAvailableReferralReward"> | null;
  activeSignupReward?: Models<"DisplayActiveReward"> | null;
};
export function useReferralRewardsFeature(): Result {
  const { data: platform } = useQuery({ ...getPlatformCurrent.keyed() });

  const { data: user } = useQuery({ ...getUser.keyed() });
  // Guests have no home location and are never shown the feature; without the guard these two
  // reads went out with an empty location segment and could only 404.
  const platformLocationId = user?.platformLocation?.id;

  const { data: referralRewardForPlatformLocation } = useQuery({
    ...getIncentivesByPlatformLocationIdAvailableReferralReward.keyed({
      platformLocationId: platformLocationId ?? "",
    }),
    enabled: !!platformLocationId,
  });

  const { data: activeSignupReward } = useQuery({
    ...getIncentivesByPlatformLocationIdActiveSignupReward.keyed({
      platformLocationId: platformLocationId ?? "",
    }),
    enabled: !!platformLocationId,
  });

  const { data: referralHistoryForPerson } = useQuery({
    ...getIncentivesByPlatformIdReferralHistoryByPersonId.keyed({
      platformId: platform?.id || "",
      personId: user?.id || "",
    }),
    enabled: !!platform?.id && !!user?.id,
  });

  const shouldShowReferralRewards = () => {
    if (referralHistoryForPerson?.referralHistory?.length) {
      return true;
    }

    return !!referralRewardForPlatformLocation && !!activeSignupReward && !user?.isGuest;
  };

  return {
    referralRewardForPlatformLocation,
    activeSignupReward,
    showReferralRewards: shouldShowReferralRewards(),
    showLegacyReferral: !user?.isGuest && !referralRewardForPlatformLocation,
  };
}
